import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Check, Copy, Loader2, Link2, MessageCircle, Mic, ShieldCheck, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { getLineBinding, bindLine, generateLiff, unbindLine, copyToClipboard } from '../../../api/lineOa.js';
import { LineMenuBuilder } from './LineMenuBuilder.jsx';
import { overlayDismiss } from '../../../lib/overlayDismiss';

const LINE_GREEN = '#06C755';

const GROUP_MODES = [
    { key: 'mention', rec: true },
    { key: 'all' },
    { key: 'off' },
];
const GROUP_MODE_KEYS = {
    mention: { label: 'line.bind.groupMentionLabel', desc: 'line.bind.groupMentionDesc' },
    all: { label: 'line.bind.groupAllLabel', desc: 'line.bind.groupAllDesc' },
    off: { label: 'line.bind.groupOffLabel', desc: 'line.bind.groupOffDesc' },
};
const VIEW_TYPES = ['compact', 'tall', 'full'];

function Section({ n, title, tag, tagColor, children }) {
    return (
        <section className="border-b border-cream-200 py-5 last:border-b-0">
            <div className="flex items-center gap-2.5 mb-1">
                <span className="size-5 grid place-items-center rounded bg-cream-100 border border-cream-200 text-[11px] font-bold text-ink-500">{n}</span>
                <h3 className="text-sm font-semibold text-ink-900">{title}</h3>
                <span className="flex-1" />
                {tag && <span className="text-[10px] font-bold px-2 py-0.5 rounded" style={tagColor}>{tag}</span>}
            </div>
            {children}
        </section>
    );
}

function Field({ label, hint, children }) {
    return (
        <label className="flex flex-col gap-1.5">
            <span className="text-xs font-semibold text-ink-600">{label}</span>
            {children}
            {hint && <span className="text-[11px] text-ink-400 leading-relaxed">{hint}</span>}
        </label>
    );
}

const inputCls = 'w-full text-sm text-ink-900 bg-white border border-cream-300 rounded-lg px-3 py-2 outline-none focus:border-[#06C755] focus:ring-2 focus:ring-[#06C755]/25 transition font-mono';

/** 遮罩輸入 + 眼睛切換（Channel Secret / Access Token 用，預設遮起來） */
function SecretInput({ value, onChange, placeholder }) {
    const { t } = useTranslation('workspace');
    const [show, setShow] = useState(false);
    return (
        <div className="relative">
            <input type={show ? 'text' : 'password'} className={inputCls + ' pr-10'} value={value} onChange={onChange} placeholder={placeholder} name="lineSecretField" autoComplete="new-password" data-lpignore="true" data-1p-ignore data-form-type="other" spellCheck={false} />
            <button type="button" tabIndex={-1} onClick={() => setShow((s) => !s)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-ink-400 hover:text-ink-700 transition"
                title={show ? t('line.common.hide') : t('line.common.show')}>
                {show ? <EyeOff size={15} /> : <Eye size={15} />}
            </button>
        </div>
    );
}

function Toggle({ on, onChange }) {
    return (
        <button type="button" role="switch" aria-checked={on} onClick={() => onChange(!on)}
            className="relative w-11 h-6 rounded-full transition shrink-0"
            style={{ background: on ? LINE_GREEN : '#cdd3cd' }}>
            <span className="absolute top-0.5 left-0.5 size-5 rounded-full bg-white shadow transition-transform" style={{ transform: on ? 'translateX(20px)' : 'none' }} />
        </button>
    );
}

export function LineBindModal({ open, onClose, avatarId, avatarName }) {
    const { t } = useTranslation('workspace');
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState(null);
    const [ok, setOk] = useState(null);
    const [b, setB] = useState(null);          // 綁定狀態（後端回）
    const [copied, setCopied] = useState('');
    const [conflict, setConflict] = useState(null);  // 此 OA 已綁別角色 → { name, avatarId }
    const [view, setView] = useState('bind');        // 'bind' | 'menu'

    // 表單
    const [channelId, setChannelId] = useState('');
    const [oaId, setOaId] = useState('');
    const [channelSecret, setChannelSecret] = useState('');
    const [channelAccessToken, setChannelAccessToken] = useState('');
    const [useWebhook, setUseWebhook] = useState(true);
    const [groupMode, setGroupMode] = useState('mention');
    const [imageCard, setImageCard] = useState(true);

    const bound = !!(b && b.bound);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        setErr(null);
        try {
            const r = await getLineBinding(avatarId);
            setB(r);
            if (r.bound) {
                setChannelId(r.channelId || '');
                setOaId(r.oaId || '');
                setChannelSecret(r.channelSecret || '');
                setChannelAccessToken(r.channelAccessToken || '');
                setUseWebhook(r.useWebhook !== false);
                setGroupMode(r.groupResponseMode || 'mention');
                setImageCard(r.imageCardReply !== false);
            }
        } catch (e) { setErr(e.message); }
        finally { setLoading(false); }
    }, [avatarId]);

    // 綁定後靜默刷新（不顯示 loading spinner）→ 不卸載區塊、保持捲動位置

    useEffect(() => { if (open) { setErr(null); setOk(null); setConflict(null); setView('bind'); setChannelSecret(''); setChannelAccessToken(''); load(); } }, [open, load]);

    if (!open) return null;

    const copy = (key, val) => { copyToClipboard(val); setCopied(key); setTimeout(() => setCopied(''), 1400); };

    async function doBind(takeover = false) {
        setSaving(true); setErr(null); setOk(null);
        if (takeover) setConflict(null);
        try {
            const r = await bindLine({
                avatarId, channelId: channelId.trim(), oaId: oaId.trim() || undefined,
                channelSecret: channelSecret || undefined, channelAccessToken: channelAccessToken || undefined,
                useWebhook, groupResponseMode: groupMode, imageCardReply: imageCard,
                takeover: takeover || undefined,
            });
            setConflict(null);
            setOk(r.webhookVerified ? t('line.bind.okBoundVerified') : (useWebhook ? t('line.bind.okBoundUnverified', { detail: r.webhookDetail || '' }) : t('line.bind.okBoundNoWebhook')));
            await load(true);   // 靜默刷新，不跳回頂端
        } catch (e) {
            // 此 OA 已綁在別的角色 → 跳「改綁到這裡」確認
            if (e.status === 409 && e.body?.code === 'ALREADY_BOUND') {
                setConflict({ name: e.body.boundAvatarName || e.body.boundAvatarId, avatarId: e.body.boundAvatarId });
            } else {
                setErr(e.body?.message || e.message);
            }
        } finally { setSaving(false); }
    }

    async function doUnbind() {
        if (!window.confirm(t('line.bind.unbindConfirm'))) return;
        setSaving(true); setErr(null);
        try { await unbindLine(avatarId); setB({ bound: false }); setChannelId(''); setOk(t('line.bind.okUnbound')); }
        catch (e) { setErr(e.body?.message || e.message); }
        finally { setSaving(false); }
    }

    if (view === 'menu') {
        return (
            <div className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
                <div className="bg-cream-50 border border-cream-200 rounded-xl shadow-2xl w-full max-w-xl max-h-[88vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
                    <LineMenuBuilder avatarId={avatarId} binding={b} onBack={() => setView('bind')} />
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 bg-ink-900/40 backdrop-blur-sm z-50 grid place-items-center p-4" {...overlayDismiss(onClose)}>
            <div className="bg-cream-50 border border-cream-200 rounded-xl shadow-2xl w-full max-w-xl max-h-[88vh] flex flex-col" onClick={(e) => e.stopPropagation()}>

                {/* header */}
                <div className="flex items-start gap-3 px-5 py-4 border-b border-cream-200">
                    <span className="size-10 rounded-lg grid place-items-center text-white font-extrabold text-lg shrink-0" style={{ background: LINE_GREEN }}>L</span>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-base font-bold text-ink-900">{t('line.bind.title')}</h2>
                        <p className="text-xs text-ink-500 mt-0.5">{t('line.bind.subtitle', { name: avatarName || t('line.bind.thisAgent') })}</p>
                    </div>
                    {bound
                        ? <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full inline-flex items-center gap-1.5 shrink-0" style={{ color: LINE_GREEN, background: '#06c7551a', border: '1px solid #06c75540' }}><ShieldCheck size={12} />{t('line.bind.bound')}</span>
                        : <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-amber-50 text-amber-700 border border-amber-200 shrink-0">{t('line.bind.notBound')}</span>}
                    <button onClick={onClose} className="size-8 grid place-items-center rounded-md text-ink-400 hover:text-ink-700 hover:bg-cream-100 transition shrink-0"><X size={16} /></button>
                </div>

                {/* body */}
                <div className="px-5 flex-1 overflow-y-auto">
                    {loading ? (
                        <div className="py-16 grid place-items-center text-ink-400"><Loader2 className="animate-spin" size={22} /></div>
                    ) : (
                        <>
                            {/* 1. Messaging API */}
                            <Section n="1" title={t('line.bind.sec1Title')} tag={t('line.common.required')} tagColor={{ background: '#e5484d1a', color: '#d63b40' }}>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Channel ID"><input className={inputCls} value={channelId} onChange={(e) => setChannelId(e.target.value)} placeholder="2002xxxxxx" name="lineChannelId" autoComplete="off" data-lpignore="true" data-1p-ignore data-form-type="other" spellCheck={false} /></Field>
                                    <Field label={t('line.bind.oaIdLabel')}><input className={inputCls} value={oaId} onChange={(e) => setOaId(e.target.value)} placeholder="@xxxxx" /></Field>
                                    <div className="col-span-2"><Field label="Channel Secret" hint={bound ? t('line.bind.secretHintSet') : null}><SecretInput value={channelSecret} onChange={(e) => setChannelSecret(e.target.value)} placeholder={bound ? t('line.bind.secretPlaceholderSet') : ''} /></Field></div>
                                    <div className="col-span-2"><Field label={t('line.bind.tokenLabel')} hint={bound ? t('line.bind.secretHintSet') : null}><SecretInput value={channelAccessToken} onChange={(e) => setChannelAccessToken(e.target.value)} placeholder={bound ? t('line.bind.secretPlaceholderSet') : t('line.bind.tokenPlaceholder')} /></Field></div>
                                </div>
                            </Section>

                            {/* 2. Webhook */}
                            <Section n="2" title={t('line.bind.sec2Title')} tag={b?.webhookVerified ? t('line.bind.verified') : null} tagColor={{ background: '#06c7551a', color: '#0a8f46' }}>
                                <div className="flex items-center gap-3 px-3 py-2.5 bg-cream-100 border border-cream-200 rounded-lg mb-3">
                                    <div className="flex-1">
                                        <div className="text-[13px] font-semibold text-ink-800">{t('line.bind.enableWebhook')}</div>
                                        <div className="text-xs text-ink-500">{t('line.bind.enableWebhookDesc')}</div>
                                    </div>
                                    <Toggle on={useWebhook} onChange={setUseWebhook} />
                                </div>
                                {bound && b.webhookUrl && (
                                    <Field label={t('line.bind.webhookUrlLabel')} hint={t('line.bind.webhookUrlHint')}>
                                        <div className="flex gap-2">
                                            <input readOnly value={b.webhookUrl} className={inputCls + ' bg-cream-100 text-ink-500 text-xs'} />
                                            <button onClick={() => copy('wh', b.webhookUrl)} className="shrink-0 px-3 rounded-lg border border-cream-300 text-xs font-semibold text-ink-600 hover:border-ink-300 inline-flex items-center gap-1">{copied === 'wh' ? <Check size={13} /> : <Copy size={13} />}{copied === 'wh' ? t('line.common.copied') : t('line.common.copy')}</button>
                                        </div>
                                    </Field>
                                )}
                            </Section>

                            {/* 3. 頁面與選單入口（往子頁：LIFF + Rich Menu）*/}
                            <Section n="3" title={t('line.bind.sec3Title')} tag={bound ? null : t('line.bind.needBindFirst')} tagColor={{ background: 'color-mix(in srgb,#c9821a 20%,transparent)', color: '#c9821a' }}>
                                <button
                                    disabled={!bound}
                                    onClick={() => setView('menu')}
                                    className="w-full flex items-center gap-3 px-3.5 py-3 rounded-xl border border-cream-200 bg-cream-100/60 hover:border-[#06C755] hover:bg-[#06c7550a] transition disabled:opacity-45 disabled:cursor-not-allowed text-left">
                                    <MessageCircle size={18} className="shrink-0" style={{ color: LINE_GREEN }} />
                                    <span className="flex-1 min-w-0">
                                        <span className="block text-[13.5px] font-semibold text-ink-800">{t('line.bind.menuEntryTitle')}</span>
                                        <span className="block text-xs text-ink-500 mt-0.5">{t('line.bind.menuEntryDesc')}</span>
                                    </span>
                                    <span className="text-ink-400 text-lg shrink-0">›</span>
                                </button>
                            </Section>

                            {/* 4. Image card */}
                            <Section n="4" title={t('line.bind.sec4Title')} tag={t('line.bind.auto')} tagColor={{ background: '#06c7551a', color: '#0a8f46' }}>
                                <div className="flex items-center gap-3 px-3 py-2.5 bg-cream-100 border border-cream-200 rounded-lg">
                                    <div className="flex-1">
                                        <div className="text-[13px] font-semibold text-ink-800">{t('line.bind.enableImageCard')}</div>
                                        <div className="text-xs text-ink-500">{t('line.bind.enableImageCardDesc')}</div>
                                    </div>
                                    <Toggle on={imageCard} onChange={setImageCard} />
                                </div>
                            </Section>

                            {/* 5. Group */}
                            <Section n="5" title={t('line.bind.sec5Title')}>
                                <p className="text-[11px] text-ink-500 mb-2.5">{t('line.bind.groupIntro')}<b>{t('line.bind.groupIntroBold')}</b></p>
                                <div className="flex flex-col gap-2">
                                    {GROUP_MODES.map((m) => (
                                        <button key={m.key} onClick={() => setGroupMode(m.key)}
                                            className="flex items-start gap-3 text-left px-3 py-2.5 rounded-lg border transition"
                                            style={groupMode === m.key ? { borderColor: LINE_GREEN, background: '#06c7550d' } : { borderColor: '#e2e6e2', background: '#fff' }}>
                                            <span className="size-4 rounded-full border-2 grid place-items-center mt-0.5 shrink-0" style={{ borderColor: groupMode === m.key ? LINE_GREEN : '#cdd3cd' }}>
                                                {groupMode === m.key && <span className="size-2 rounded-full" style={{ background: LINE_GREEN }} />}
                                            </span>
                                            <span>
                                                <span className="text-[13px] font-semibold text-ink-800 flex items-center gap-2">{t(GROUP_MODE_KEYS[m.key].label)}{m.rec && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ color: LINE_GREEN, background: '#06c7551a' }}>{t('line.common.recommended')}</span>}</span>
                                                <span className="block text-xs text-ink-500 mt-0.5">{t(GROUP_MODE_KEYS[m.key].desc)}</span>
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            </Section>
                        </>
                    )}
                </div>

                {/* 衝突：此 OA 已綁在別的角色 → 一鍵改綁 */}
                {conflict && (
                    <div className="px-5 py-3 border-t border-amber-200 bg-amber-50 flex items-start gap-3">
                        <AlertTriangle size={17} className="text-amber-500 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-amber-800">{t('line.bind.conflictTitle', { name: conflict.name })}</div>
                            <div className="text-xs text-amber-700 mt-0.5">{t('line.bind.conflictDesc', { name: avatarName || t('line.bind.thisAgent') })}</div>
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                            <button onClick={() => setConflict(null)} className="px-3 py-1.5 text-xs rounded-lg text-amber-700 hover:bg-amber-100 transition">{t('line.common.cancel')}</button>
                            <button onClick={() => doBind(true)} disabled={saving} className="px-3.5 py-1.5 text-xs font-semibold rounded-lg text-white disabled:opacity-40 inline-flex items-center gap-1.5" style={{ background: LINE_GREEN }}>
                                {saving ? <Loader2 size={13} className="animate-spin" /> : <Link2 size={13} />}{t('line.bind.conflictAction')}
                            </button>
                        </div>
                    </div>
                )}

                {/* footer */}
                <div className="px-5 py-3.5 border-t border-cream-200 bg-cream-100/50 flex items-center gap-2">
                    {err && <span className="flex-1 text-xs text-danger-600 inline-flex items-center gap-1.5 min-w-0"><AlertTriangle size={13} className="shrink-0" /><span className="truncate">{err}</span></span>}
                    {!err && ok && <span className="flex-1 text-xs inline-flex items-center gap-1.5 min-w-0" style={{ color: LINE_GREEN }}><Check size={13} className="shrink-0" /><span className="truncate">{ok}</span></span>}
                    {!err && !ok && <span className="flex-1 text-[11px] text-ink-400">{t('line.bind.footerHint')}</span>}
                    {bound && <button onClick={doUnbind} disabled={saving} className="px-3 py-2 text-xs rounded-lg text-danger-600 hover:bg-danger-50 transition">{t('line.bind.unbind')}</button>}
                    <button onClick={onClose} className="px-4 py-2 text-sm rounded-lg text-ink-700 hover:bg-cream-200 transition">{t('line.common.close')}</button>
                    <button onClick={() => doBind()} disabled={saving || loading || !channelId.trim()} className="px-5 py-2 text-sm font-semibold rounded-lg text-white disabled:opacity-40 inline-flex items-center gap-1.5" style={{ background: LINE_GREEN }}>
                        {saving ? <Loader2 size={14} className="animate-spin" /> : <Link2 size={14} />}{t('line.bind.save')}
                    </button>
                </div>
            </div>
        </div>
    );
}
